/**
 * CommissariatAuxComptesService — exercices CAC (`/api/v1/commissariat-aux-comptes/`).
 *
 * « Exercice CAC » = société + période, portant l'opinion, les réserves (nested),
 * et les documents (lettre mission, matrice, plan d'action, rapports — fichiers
 * uploadés via /files/). Action `demarrer_suivi` (corps ignoré côté backend).
 * Pas de filtre `societe` côté serveur → filtrage client.
 */

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DrfPaginatedResponse } from '../../../../core/models/api.model';
import { buildHttpParams, QueryFilters } from '../../../../shared/core/services/pagination.util';
import {
    CommissariatAuxComptes,
    CommissariatAuxComptesCreateRequest,
    CommissariatAuxComptesCreateResponse,
    PatchedCommissariatAuxComptesPartialUpdateRequest,
} from '../models/cac-api.model';

/** Filtres serveur exposés par `GET /commissariat-aux-comptes/`. */
export type CommissariatCacListFilters = QueryFilters & {
    page?: number;
    per_page?: number;
    id?: string;
    valide?: boolean;
    annee_debut?: number;
    annee_debut__gte?: number;
    annee_debut__lte?: number;
    annee_fin?: number;
    annee_fin__gte?: number;
    annee_fin__lte?: number;
    opinion_formulee__code?: string;
    opinion_formulee__code__in?: string;
};

@Injectable({ providedIn: 'root' })
export class CommissariatAuxComptesService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = `${environment.apiUrl}/commissariat-aux-comptes/`;

    list(filters: CommissariatCacListFilters = {}): Observable<DrfPaginatedResponse<CommissariatAuxComptes>> {
        return this.http.get<DrfPaginatedResponse<CommissariatAuxComptes>>(this.endpoint, {
            params: buildHttpParams(filters),
        });
    }

    retrieve(id: string): Observable<CommissariatAuxComptes> {
        return this.http.get<CommissariatAuxComptes>(`${this.endpoint}${id}/`);
    }

    /**
     * POST — la réponse est **tronquée** par le serializer de création
     * (id, societe, annee_debut, annee_fin) : recharger l'exercice pour en obtenir la forme complète.
     */
    create(payload: CommissariatAuxComptesCreateRequest): Observable<CommissariatAuxComptesCreateResponse> {
        return this.http.post<CommissariatAuxComptesCreateResponse>(this.endpoint, payload);
    }

    partialUpdate(id: string, payload: PatchedCommissariatAuxComptesPartialUpdateRequest): Observable<CommissariatAuxComptes> {
        return this.http.patch<CommissariatAuxComptes>(`${this.endpoint}${id}/`, payload);
    }

    destroy(id: string): Observable<void> {
        return this.http.delete<void>(`${this.endpoint}${id}/`);
    }

    /** POST /{id}/demarrer_suivi/ — valide l'exercice (passe `valide=true`, verrouillage lecture seule). Corps ignoré côté backend. */
    demarrerSuivi(id: string): Observable<void> {
        return this.http.post<void>(`${this.endpoint}${id}/demarrer_suivi/`, {});
    }
}
